/** One glossary term pair carried in `config.context`. */
export interface TranslationTerm {
  source?: string
  target?: string
}

/** Shape of `config.context` on the STT proxy WebSocket. */
export interface SttConfigContext {
  text?: string
  terms?: string[]
  /** Canonical field name the proxy reads. */
  translationTerms?: TranslationTerm[]
  /**
   * @deprecated Legacy snake_case alias, accepted on input only. Folded into
   * {@link SttConfigContext.translationTerms} by {@link normalizeSttConfigContext}.
   */
  translation_terms?: TranslationTerm[]
  general?: Array<{ key?: string; value?: string }>
}

/**
 * Converts local context naming into the payload shape expected by the STT proxy.
 *
 * Both aliases are destructured out, so the result carries `translationTerms`
 * only when there is something to send — never as `undefined`.
 */
export function normalizeSttConfigContext(
  context?: SttConfigContext | null
): SttConfigContext | undefined {
  if (!context) return undefined

  const { translationTerms, translation_terms: legacyTranslationTerms, ...rest } = context
  const resolvedTranslationTerms = translationTerms ?? legacyTranslationTerms

  return resolvedTranslationTerms ? { ...rest, translationTerms: resolvedTranslationTerms } : rest
}

/** True when normalized context carries anything minutes upstream should see. */
function sttConfigContextHasMinutesReference(
  context: SttConfigContext
): boolean {
  if (context.text?.trim()) return true
  if (context.terms?.some((term) => term.trim())) return true
  if (
    context.translationTerms?.some(
      (term) => term.source?.trim() || term.target?.trim()
    )
  ) {
    return true
  }
  if (
    context.general?.some(
      (entry) => entry.key?.trim() || entry.value?.trim()
    )
  ) {
    return true
  }
  return false
}

/**
 * Serializes setup context for upstream minutes `context` (reference-only string).
 * Matches the normalized shape previously sent on the internal STT `config` frame.
 */
export function serializeSttConfigContextForMinutesReference(
  context?: SttConfigContext | null
): string | undefined {
  const normalized = normalizeSttConfigContext(context)
  if (!normalized || !sttConfigContextHasMinutesReference(normalized)) return undefined
  return JSON.stringify(normalized)
}
